import { useEffect, useState } from 'react';
import type { OAuthProviderInfo, OAuthProvidersResponse } from '@authsemply/shared';
import { api } from '../lib/api';
import { PRIVACY_URL, TERMS_URL } from '../lib/legal';
import { GoogleButton } from './GoogleButton';

/** « Google », « Google ou Microsoft », « Google, Microsoft ou Apple ». */
function enumerer(libelles: string[]): string {
  if (libelles.length <= 1) return libelles.join('');
  return `${libelles.slice(0, -1).join(', ')} ou ${libelles[libelles.length - 1]}`;
}

/**
 * Fournisseurs actifs, demandés UNE fois par chargement du portail.
 *
 * La liste vient de l'API (`GET /oauth/providers`) : un fournisseur absent du
 * .env n'y figure pas, et son bouton n'existe pas. En cas d'échec de l'appel,
 * aucun bouton — mieux vaut un bouton manquant qu'un bouton qui mène à une
 * erreur.
 */
let chargement: Promise<OAuthProviderInfo[]> | null = null;
function fournisseursActifs(): Promise<OAuthProviderInfo[]> {
  chargement ??= api
    .get<OAuthProvidersResponse>('/oauth/providers')
    .then((reponse) => reponse.providers ?? [])
    .catch(() => {
      chargement = null; // nouvel essai au prochain affichage
      return [];
    });
  return chargement;
}

interface Props {
  /**
   * Connexion, inscription (libellé + mention CGU) ou reconnexion exigée par
   * un produit (« Confirmer avec … », compte de la session imposé).
   */
  mode: 'login' | 'register' | 'reconnexion';
  /** URL /oidc/authorize à reprendre après le retour du fournisseur. */
  returnTo?: string | null;
  /** Liste imposée (reconnexion : les fournisseurs RATTACHÉS au compte). */
  providers?: OAuthProviderInfo[];
  /** Pas de séparateur « ou » (rien au-dessus des boutons). */
  sansSeparateur?: boolean;
}

/**
 * Séparateur « ou » + un bouton par fournisseur externe actif (Google,
 * Microsoft, Apple). Ne rend RIEN si aucun n'est configuré.
 */
export function SocialButtons({ mode, returnTo, providers: imposes, sansSeparateur }: Props) {
  const [actifs, setProviders] = useState<OAuthProviderInfo[]>([]);
  const providers = imposes ?? actifs;
  const reconnexion = mode === 'reconnexion';

  useEffect(() => {
    if (imposes) return;
    let vivant = true;
    void fournisseursActifs().then((liste) => {
      if (vivant) setProviders(liste);
    });
    return () => {
      vivant = false;
    };
  }, [imposes]);

  if (providers.length === 0) return null;

  const libelle = (label: string) =>
    mode === 'register'
      ? `S'inscrire avec ${label}`
      : reconnexion
        ? `Confirmer avec ${label}`
        : `Continuer avec ${label}`;

  return (
    <>
      {sansSeparateur ? null : <div className="divider">ou</div>}
      <div className="social-buttons">
        {providers.map((provider) =>
          provider.id === 'google' ? (
            <GoogleButton
              key="google"
              label={libelle(provider.label)}
              returnTo={returnTo}
              reconnexion={reconnexion}
            />
          ) : (
            <a
              key={provider.id}
              className={`btn btn-secondary btn-oauth btn-${provider.id}`}
              href={api.oauthUrl(provider.id, returnTo ?? undefined, reconnexion)}
            >
              <ProviderLogo id={provider.id} />
              {libelle(provider.label)}
            </a>
          ),
        )}
      </div>
      {/* Le formulaire fait accepter les CGU par une case ; le parcours externe
          n'ayant pas de case, l'acceptation est portée par cette mention. */}
      {mode === 'register' ? (
        <p className="terms-note">
          En continuant avec {enumerer(providers.map((provider) => provider.label))}, un écran
          de confirmation vous présentera le compte à créer, avec les{' '}
          <a href={TERMS_URL} target="_blank" rel="noreferrer">
            conditions générales d'utilisation et de vente
          </a>{' '}
          et la{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
            politique de confidentialité
          </a>
          .
        </p>
      ) : null}
    </>
  );
}

/**
 * Logo officiel du fournisseur, lu dans `public/logos/<id>.svg`.
 *
 * Les fichiers ne sont PAS dans le dépôt : ils se téléchargent depuis les kits
 * de marque de Microsoft et d'Apple, dont les conditions d'usage imposent les
 * versions exactes (cf. docs/connexion-externe.md). Tant qu'un fichier manque,
 * le bouton s'affiche sans logo — `onError` masque l'image.
 */
function ProviderLogo({ id }: { id: string }) {
  const [absent, setAbsent] = useState(false);
  if (absent) return null;
  return (
    <img
      src={`/logos/${id}.svg`}
      width={18}
      height={18}
      alt=""
      aria-hidden="true"
      onError={() => setAbsent(true)}
    />
  );
}
